import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

declare global {
  interface Window {
    WOW: any;
  }
}

interface TeamMember {
  id: number;
  name: string;
  role: string;
  image: string;
  bio?: string;
  facebook?: string;
  twitter?: string;
  linkedin?: string;
  instagram?: string;
  active: boolean;
  order_position?: number;
}

const Team: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch team members from API
    fetch('http://localhost:5000/api/team?active=true')
      .then(res => res.json())
      .then(data => {
        console.log('Team data received:', data);
        data.forEach((member: TeamMember) => {
          console.log(`Team member ${member.name} image path:`, member.image);
        });
        setTeamMembers(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching team members:', error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    // Initialize data-background attributes
    const elements = document.querySelectorAll('[data-background]');
    elements.forEach((element) => {
      const bg = element.getAttribute('data-background');
      if (bg) {
        (element as HTMLElement).style.backgroundImage = `url(${bg})`;
      }
    });

    // Initialize WOW.js for scroll animations
    if (window.WOW) {
      const wow = new window.WOW({
        boxClass: 'wow',
        animateClass: 'animated',
        offset: 0,
        mobile: true,
        live: true
      });
      wow.init();
    }
  }, [teamMembers]);

  return (
    <>
      {/* Page banner area start here */}
      <section className="banner__inner-page bg-image pt-160 pb-160"
        data-background="/assets/images/banner/banner-inner-page.jpg">
        <div className="container">
          <h2 className="wow fadeInUp" data-wow-delay="00ms" data-wow-duration="1500ms">Our Team</h2>
          <div className="breadcrumb-list wow fadeInUp" data-wow-delay="200ms" data-wow-duration="1500ms">
            <Link to="/">Home</Link>
            <span><i className="fa-regular fa-angles-right mx-2"></i>Our Team</span>
          </div>
        </div>
      </section>
      {/* Page banner area end here */}

      {loading ? (
        <div className="text-center py-5">
          <p>Loading team members...</p>
        </div>
      ) : (
        <>
          {/* Team area start here */}
          <section className="team-area pt-120 pb-120">
            <div className="container">
              {(() => {
                const coreMembers = teamMembers.filter((m) => Number(m.order_position || 0) < 1000);
                const supporterMembers = teamMembers.filter((m) => Number(m.order_position || 0) >= 1000);

                const renderMemberCard = (member: TeamMember, index: number, isLarge: boolean) => (
                  <div
                    key={member.id}
                    className={`${isLarge ? 'col-lg-4 col-sm-6' : 'col-lg-3 col-sm-6'} wow fadeInUp`}
                    data-wow-delay={`${Math.min(index, 6) * 100}ms`}
                    data-wow-duration="1500ms"
                  >
                    <div className="team__item image">
                      <img
                        src={member.image || '/assets/images/team/team-image1.jpg'}
                        alt={member.name}
                        style={{ width: '100%', height: isLarge ? '451px' : '340px', objectFit: 'cover' }}
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/team/team-image1.jpg';
                        }}
                      />
                      <div className="team__content">
                        <div className="social-icon mb-30">
                          {member.facebook && (
                            <a href={member.facebook} target="_blank" rel="noopener noreferrer">
                              <i className="fa-brands fa-facebook-f"></i>
                            </a>
                          )}
                          {member.instagram && (
                            <a href={member.instagram} target="_blank" rel="noopener noreferrer" className="active">
                              <i className="fa-brands fa-instagram"></i>
                            </a>
                          )}
                          {member.linkedin && (
                            <a href={member.linkedin} target="_blank" rel="noopener noreferrer">
                              <i className="fa-brands fa-linkedin-in"></i>
                            </a>
                          )}
                          {member.twitter && (
                            <a href={member.twitter} target="_blank" rel="noopener noreferrer">
                              <i className="fa-brands fa-twitter"></i>
                            </a>
                          )}
                        </div>
                        <div className="content">
                          <h4>
                            <Link to={`/team/${member.id}`} className="primary-hover">
                              {member.name}
                            </Link>
                          </h4>
                          <span>{member.role}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                );

                return (
                  <>
                    {coreMembers.length > 0 && (
                      <>
                        <div className="section-header mb-40 text-center">
                          <h2>Core Team Members</h2>
                        </div>
                        <div className="row g-4 mb-5">
                          {coreMembers.slice(0, 3).map((member, index) => renderMemberCard(member, index, true))}
                        </div>
                        {coreMembers.length > 3 && (
                          <div className="row g-4 mb-5">
                            {coreMembers.slice(3).map((member, index) => renderMemberCard(member, index, false))}
                          </div>
                        )}
                      </>
                    )}

                    {supporterMembers.length > 0 && (
                      <>
                        <div className="section-header mb-40 text-center" style={{ marginTop: '20px' }}>
                          <h2>Our Supporters</h2>
                        </div>
                        <div className="row g-4">
                          {supporterMembers.map((member, index) => renderMemberCard(member, index, false))}
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </section>
          {/* Team area end here */}
        </>
      )}
    </>
  );
};

export default Team;
